/**
 * TopCausesWidget — the root-cause categories behind incidents, ranked.
 *
 * Source of the taxonomy: `incidents.tags`, written by the app's own "Root
 * Cause Categories" picker on the report form and the incident detail page.
 * The categories come from the DATA — this file contains no cause list, so a
 * tenant that renames or stops using a category sees that immediately.
 *
 * Two honesty details the chart alone cannot carry:
 * - An incident may be tagged with several categories, so the bars sum to more
 *   than the incident count. Said once, under the chart.
 * - Incidents with no category recorded are not "no cause" — they are unknown.
 *   The coverage line reports them instead of letting the chart imply a total.
 *
 * Click a bar → the incidents carrying that category (real records, opening to
 * the incident itself).
 */
import React, { useState } from 'react';
import { useDashboard } from '../context/DashboardContext';
import { useDashboardData } from '../hooks/useDashboardData';
import { fetchIncidentCauses } from '../api/client';
import ChartWidget from './ChartWidget';
import DrilldownModal from './DrilldownModal';
import { CARD, HEADING } from '../ui';

const TOP_N = 10;
const ALL_N = 50;

export default function TopCausesWidget({ title = 'Top Causes of Incidents', limit = TOP_N }) {
  const { siteId, time, departmentId, dimensions, fyStartMonth, refreshMs } = useDashboard();
  const [showAll, setShowAll] = useState(false);
  const [drill, setDrill] = useState(null);

  const effectiveLimit = showAll ? ALL_N : limit;
  const timeKey = time ? JSON.stringify(time) : '';
  const dimsKey = dimensions ? JSON.stringify(dimensions) : '';

  const { data, loading, error } = useDashboardData(
    (signal) => {
      const body = { limit: effectiveLimit };
      if (siteId) body.site_id = siteId;
      if (departmentId) body.department_id = departmentId;
      if (dimensions && Object.keys(dimensions).length) body.dimensions = dimensions;
      if (time) body.time = time;
      if (fyStartMonth) body.fy_start_month = fyStartMonth;
      return fetchIncidentCauses(body, { signal });
    },
    [siteId, timeKey, departmentId, dimsKey, effectiveLimit, fyStartMonth],
    { refreshMs },
  );

  const rows = data?.data || [];
  const coverage = data?.coverage;
  const drillMetric = data?.drill?.metric || 'safety.incidents.total';
  const drillDimension = data?.drill?.dimension || 'cause';

  // No cause categories recorded at all is a different state from "no
  // incidents" — say which, so nobody reads a blank chart as good news.
  const nothingTagged = !loading && !error && rows.length === 0;

  return (
    <>
      <div className={`${CARD} p-4`}>
        <div className="mb-3 flex items-start justify-between gap-3">
          <h3 className={HEADING}>{title}</h3>
          {data?.truncated && !showAll && (
            <button
              type="button"
              onClick={() => setShowAll(true)}
              className="shrink-0 text-xs font-medium text-blue-600 hover:text-blue-800"
            >
              View all {data.total_causes}
            </button>
          )}
          {showAll && (
            <button
              type="button"
              onClick={() => setShowAll(false)}
              className="shrink-0 text-xs font-medium text-blue-600 hover:text-blue-800"
            >
              Show top {limit}
            </button>
          )}
        </div>

        {nothingTagged ? (
          <div className="py-10 text-center text-sm text-gray-400">
            {coverage?.incidents
              ? `No root-cause categories recorded on the ${coverage.incidents} incident${
                coverage.incidents === 1 ? '' : 's'} in this period.`
              : 'No data available for the selected filters.'}
          </div>
        ) : (
          <ChartWidget
            type="hbar"
            data={rows}
            xKey="label"
            series={data?.series || [{ key: 'value', label: 'Incidents' }]}
            height={Math.max(240, rows.length * 30 + 40)}
            showValues
            loading={loading}
            error={error}
            onPointClick={(d) => {
              const key = d?.key ?? d?.payload?.key;
              const label = d?.label ?? d?.payload?.label;
              if (key) setDrill({ key, label });
            }}
          />
        )}

        {!loading && !error && rows.length > 0 && (
          <p className="mt-2 text-[11px] leading-relaxed text-gray-500">
            An incident can carry more than one cause category, so the bars total
            more than the incident count.
            {coverage && coverage.without_cause > 0 && (
              <>
                {' '}
                {coverage.with_cause} of {coverage.incidents} incidents have a cause
                category recorded; {coverage.without_cause} do not and are not
                counted here.
              </>
            )}
          </p>
        )}
      </div>

      <DrilldownModal
        open={!!drill}
        metric={drillMetric}
        title={`Incidents — Root Cause: ${drill?.label ?? ''}`}
        subtitle="Incidents tagged with this root-cause category. Click a row to open the incident."
        extraDimensions={drill ? { [drillDimension]: [drill.key] } : null}
        onClose={() => setDrill(null)}
      />
    </>
  );
}
